import { Button } from '@nextui-org/react'
import { FC, useCallback, useMemo } from 'react'

export type CameraPos = 'left' | 'top' | 'right'

export type CameraPositionSelectorProps = {
  selected: CameraPos | null
  onSelect: (pos: CameraPos) => void
  availablePositions?: CameraPos[] // if omitted, all are available
}

const allPositions: CameraPos[] = ['left', 'top', 'right']

export const CameraPositionSelector: FC<CameraPositionSelectorProps> = ({
  selected,
  onSelect,
  availablePositions,
}) => {
  const isAvailable = useCallback(
    (pos: CameraPos) => (availablePositions ? availablePositions.includes(pos) : true),
    [availablePositions],
  )

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = selected ? allPositions.indexOf(selected) : -1
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        const next = (() => {
          for (let i = 1; i <= allPositions.length; i++) {
            const candidate =
              allPositions[(currentIndex + i + allPositions.length) % allPositions.length]
            if (isAvailable(candidate)) return candidate
          }
          return selected ?? 'left'
        })()
        onSelect(next)
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        const prev = (() => {
          for (let i = 1; i <= allPositions.length; i++) {
            const candidate =
              allPositions[(currentIndex - i + allPositions.length) % allPositions.length]
            if (isAvailable(candidate)) return candidate
          }
          return selected ?? 'left'
        })()
        onSelect(prev)
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (selected) onSelect(selected)
      }
    },
    [selected, onSelect, isAvailable],
  )

  const buttons = useMemo(
    () =>
      allPositions.map((pos) => {
        const disabled = !isAvailable(pos)
        const isSelected = selected === pos
        return (
          <Button
            key={pos}
            type="button"
            variant="flat"
            size="sm"
            className={[
              'flex-1 capitalize border border-tdpldarkgrey border-opacity-60 min-w-0 max-w-full',
              isSelected
                ? 'bg-tdplplum text-white border-tdplhibiscus'
                : 'bg-white text-tdplindigo hover:bg-tdplbisque',
              disabled
                ? 'opacity-50 cursor-not-allowed bg-tdpllightgrey hover:bg-tdpllightgrey'
                : '',
            ].join(' ')}
            style={{ alignSelf: 'center', fontSize: '0.8rem', width: 'auto' }}
            onClick={() => !disabled && onSelect(pos)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                !disabled && onSelect(pos)
              }
            }}
            aria-pressed={isSelected}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={disabled}
            tabIndex={0}
            isDisabled={disabled}
          >
            {pos}
          </Button>
        )
      }),
    [selected, onSelect, isAvailable],
  )

  return (
    <div
      className="flex gap-2 justify-center items-stretch sm:flex-row flex-col w-full max-w-full camera-position-selector"
      role="radiogroup"
      aria-label="Select Camera Position"
      onKeyDown={handleKey}
    >
      {buttons}
    </div>
  )
}

export default CameraPositionSelector
